import * as Yup from 'yup';
import { ERROR_TYPES } from '../../constants/errorTypes';
import { getTranslation } from '../../utils/functions';
import {
  getNationalityListOptions,
  getCountryListOptions,
  COUNTRY_CODE,
} from '../../constants/countryList';
import { FIELDS_NAME } from '../../constants/fields';
import { FISCAL_CODE_REGEX } from '../../constants/regex';
// field name of role is title
const ROLE = {
  RESIDENT: 'Resident',
  SUPPLIER: 'Supplier',
  MEDIA: 'Media',
};

const inputTextSchema = ({
  required,
  properties: {
    errorMessageRequired = 'This field is required',
    maxLength = 100,
    errorMessageMaxLength = '',
    minLength = 0,
    errorMessageMinLength = '',
    digitsOnly = false,
    errorMessageDigitsOnly = '',
    isAlphabetic = false,
    isAlphaNumeric = false,
    errorMessageAlphaNumericOnly = '',
    errorMessageAlphaOnly = '',
    isFiscalCode = false,
    errorMessageFiscalCode = '',
  },
}) => {
  let validationSchema;

  if (digitsOnly) {
    validationSchema = Yup.string().matches(
      /^[0-9]+$/,
      getTranslation(errorMessageDigitsOnly),
    );
  } else {
    validationSchema = Yup.string();
  }

  if (required) {
    validationSchema = validationSchema.required(
      getTranslation(errorMessageRequired),
    );
  }

  if (minLength) {
    validationSchema = validationSchema.min(
      minLength,
      getTranslation(errorMessageMinLength),
    );
  }

  if (maxLength) {
    validationSchema = validationSchema.max(
      maxLength,
      getTranslation(errorMessageMaxLength),
    );
  }

  if (isAlphabetic) {
    validationSchema = validationSchema.matches(
      /^[a-zA-Z]+$/,
      getTranslation(errorMessageAlphaOnly),
    );
  }

  if (isAlphaNumeric) {
    validationSchema = validationSchema.matches(
      /^[a-zA-Z0-9]+$/,
      getTranslation(errorMessageAlphaNumericOnly),
    );
  }

  if (maxLength && isAlphaNumeric) {
    validationSchema = validationSchema
      .max(maxLength, getTranslation(errorMessageMaxLength))
      .matches(/^[a-zA-Z0-9]+$/, getTranslation(errorMessageAlphaNumericOnly));
  }

  if (isFiscalCode) {
    validationSchema = validationSchema.matches(
      FISCAL_CODE_REGEX,
      getTranslation(errorMessageFiscalCode),
    );
  }

  return validationSchema;
};

const inputFileUploadSingleSchema = ({ required }) => {
  if (required) {
    return Yup.string().required(ERROR_TYPES.FILE_UPLOAD_REQUIRED);
  }
  return Yup.string();
};

const inputFileUploadSingleWithWebcamSchema = ({ required }) => {
  if (required) {
    return Yup.string().required(ERROR_TYPES.FILE_UPLOAD_REQUIRED);
  }
  return Yup.string();
};

const inputNumberSchema = ({
  required,
  properties: { errorMessageRequired },
}) => {
  if (required) {
    return Yup.number().required(getTranslation(errorMessageRequired));
  }
  return Yup.number();
};

// const inputSelectSchema = ({
//   properties: { errorMessageRequired, values },
//   required,
// }) => {
//   const keys = (values ?? []).map((value) => value.key);
//   if (required) {
//     return Yup.mixed()
//       .oneOf(keys, getTranslation(errorMessageRequired))
//       .required(getTranslation(errorMessageRequired));
//   }
//   return Yup.mixed().oneOf(keys, getTranslation(errorMessageRequired));
// };

const inputSelectSchema = ({
  properties: { errorMessageRequired, values, apiValidator, isMultiSelect },
  required,
}) => {
  return Yup.mixed()
    .test(
      'is-valid-option',
      getTranslation(errorMessageRequired),
      async function (value) {
        // Handle empty values for both single and multi select
        if (isMultiSelect) {
          // For multiselect, value should be an array or a string
          if (
            !value ||
            (Array.isArray(value) && value.length === 0) ||
            value === ''
          ) {
            return !required; // if required, fail when empty
          }
        } else {
          // For single select, check if value is empty
          if (!value) {
            return !required; // if required, fail when empty
          }
        }

        // Convert value to array format for validation if multiselect
        let valuesToValidate = value;
        if (isMultiSelect) {
          if (typeof value === 'string') {
            valuesToValidate = value
              .split(',')
              .map((v) => v.trim())
              .filter((v) => v);
            if (valuesToValidate.length === 0) {
              return !required;
            }
          } else if (!Array.isArray(value)) {
            valuesToValidate = [value];
          }
        } else {
          valuesToValidate = [value];
        }

        // Ensure valuesToValidate is always an array for consistent validation
        if (!Array.isArray(valuesToValidate)) {
          valuesToValidate = [valuesToValidate];
        }

        // If static options are provided, validate against them
        if (values && values.length > 0) {
          const keys = values.map((v) => v.key);
          return valuesToValidate.every((val) => keys.includes(val));
        }

        // If API validation function is provided, call it
        if (typeof apiValidator === 'function') {
          try {
            const validationResults = await Promise.all(
              valuesToValidate.map((val) => apiValidator(val)),
            );
            return validationResults.every((result) => result);
          } catch (err) {
            return false; // fail safe if API errors
          }
        }

        return true; // fallback if nothing else
      },
    )
    .required(required ? getTranslation(errorMessageRequired) : undefined);
};

const inputCountrySchema = ({
  properties: { errorMessageRequired },
  required,
}) => {
  const keys = getCountryListOptions().map((value) => value.key);
  if (required) {
    return Yup.mixed()
      .oneOf(keys, getTranslation(errorMessageRequired))
      .required(getTranslation(errorMessageRequired));
  }
  return Yup.mixed().oneOf(keys, getTranslation(errorMessageRequired));
};

// const inputEventsListSchema = ({ properties: { errorMessageRequired }, required }) => {
//   if (required) {
//     return Yup.string().test('is-eventIds', getTranslation(errorMessageRequired), (value) => {
//       return !!value;
//     });
//   }
//   return Yup.string();
// };

const inputEventsListSchema = ({
  properties: { errorMessageRequired },
  required,
}) => {
  if (required) {
    return Yup.string().required(getTranslation(errorMessageRequired));
  }
  return Yup.string();
};

const inputStateListSchema = ({ properties: { errorMessageRequired } }) => {
  return Yup.string()
    .ensure()
    .when(FIELDS_NAME.NATIVE_RESIDENT, {
      is: 'true',
      then: Yup.string().required(getTranslation(errorMessageRequired)),
    });
};

const inputAddressStateListSchema = ({
  properties: { errorMessageRequired },
}) => {
  return Yup.string()
    .ensure()
    .when(FIELDS_NAME.ADDRESS_COUNTRY, {
      is: COUNTRY_CODE.GERMANY,
      then: Yup.string().required(getTranslation(errorMessageRequired)),
    });
};

const inputPreviousNamesListSchema = ({
  properties: { errorMessageRequired },
}) => {
  return Yup.string()
    .ensure()
    .when(FIELDS_NAME.COUNTRY_OF_BIRTH, {
      is: COUNTRY_CODE.ITALY,
      then: Yup.string().required(getTranslation(errorMessageRequired)),
    });
};

const inputJobTitleSchema = ({ properties: { errorMessageRequired } }) => {
  return Yup.string()
    .ensure()
    .when(FIELDS_NAME.TITLE, {
      is: (role) => !!(role && role !== ROLE.RESIDENT),
      then: Yup.string().required(getTranslation(errorMessageRequired)),
    });
};

// For react-select validation. It is not working. will check later.
// const inputEventsListSchema1 = ({ properties: { errorMessageRequired }, required }) => {
//   if (required) {
//     return Yup.mixed().required().oneOf(["usa", "mexico"])
//     .label("Selected Country");
//   }
//   return Yup.string();
// };

const inputNationalitySchema = ({
  properties: { errorMessageRequired },
  required,
}) => {
  const keys = getNationalityListOptions().map((value) => value.key);
  if (required) {
    return Yup.mixed()
      .oneOf(keys, getTranslation(errorMessageRequired))
      .required(getTranslation(errorMessageRequired));
  }
  return Yup.mixed().oneOf(keys, getTranslation(errorMessageRequired));
};

const inputContrySelectOrWriteSchema = ({
  properties: { errorMessageRequired },
  required,
}) => {
  if (required) {
    return Yup.object()
      .shape({
        name: Yup.string().required(getTranslation(errorMessageRequired)),
        code: Yup.string(),
      })
      .required(getTranslation(errorMessageRequired));
  }

  return Yup.object().shape({
    name: Yup.string(),
    code: Yup.string(),
  });
};

const inputToggleSchema = ({
  properties: { errorMessageRequiredValue },
  required,
}) => {
  if (required) {
    return Yup.bool().oneOf([true], getTranslation(errorMessageRequiredValue));
  }
  return Yup.bool().oneOf(
    [true, false],
    getTranslation(errorMessageRequiredValue),
  );
};

const inputCheckboxSchema = ({
  properties: { errorMessageRequired },
  required,
}) => {
  if (required) {
    return Yup.bool().oneOf([true], getTranslation(errorMessageRequired));
  }
  return Yup.bool().oneOf([true, false], getTranslation(errorMessageRequired));
};

const inputDateSchema = ({
  required,
  properties: { errorMessageRequired },
}) => {
  if (required) {
    return Yup.date('Wrong format')
      .required(getTranslation(errorMessageRequired))
      .nullable();
  }
  return Yup.date().nullable();
};

const inputEmailSchema = ({
  required,
  properties: {
    errorMessageRequired,
    label,
    maxLength = null,
    errorMessageMaxLength = '',
  },
}) => {
  let validationSchema = Yup.string().email().label(getTranslation(label));

  if (required) {
    validationSchema = validationSchema.required(
      getTranslation(errorMessageRequired),
    );
  }

  if (maxLength) {
    validationSchema = validationSchema.max(
      maxLength,
      getTranslation(errorMessageMaxLength),
    );
  }

  return validationSchema;
};

const InputsSchema = {
  TEXTFIELD: inputTextSchema,
  EMAIL: inputEmailSchema,
  PHONE_NUMBER: inputNumberSchema,
  DROPDOWN: inputSelectSchema,
  COUNTRY_SELECT: inputCountrySchema,
  COUNTRY_SELECT_OR_WRITE: inputCountrySchema,
  RADIO_BUTTONS: inputSelectSchema,
  TOGGLE_BUTTON: inputToggleSchema,
  DATE_PICKER: inputDateSchema,
  CHECKBOX: inputCheckboxSchema,
  FILE_UPLOAD_SINGLE: inputFileUploadSingleSchema,
  FILE_UPLOAD_SINGLE_WITH_WEBCAM: inputFileUploadSingleWithWebcamSchema,
  NATIONALITY_SELECT: inputNationalitySchema,
  TEXTAREA: inputTextSchema,
  EVENT_MULTI_SELECT: inputEventsListSchema,
  NATIVE_RESIDENT_SELECT: inputEventsListSchema,
  INPUT_TAGS: inputTextSchema,
};

const DynamicInputFieldSchema = {
  [FIELDS_NAME.FEDERAL_STATES]: inputStateListSchema,
  // [FIELDS_NAME.STATE]: inputAddressStateListSchema,
  [FIELDS_NAME.JOB_TITLE]: inputJobTitleSchema,
  // [FIELDS_NAME.PREVIOUS_NAMES]: inputPreviousNamesListSchema,
};

export { InputsSchema, DynamicInputFieldSchema, ROLE };
